import WCC from 'wcc'
const globalConstants = WCC && WCC.WMR_CONSTANTS ? WCC.WMR_CONSTANTS : {}

export interface Node {
  id: string
}

export interface Edge<T> {
  node: T
  cursor: string
}

export interface Connection<T> {
  edges: Array<Edge<T>>
  pageInfo: {
    hasNextPage: boolean
    hasPreviousPage: boolean
    startCursor?: string
    endCursor?: string
  }
}

export interface IShopifyGqlError {
  message: string,
  locations?: Array<{ line: number, column: number }>, 
  path?: Array<string | number>,
  extensions?: Record<string, any>
}

export class ShopifyGqlError extends Error implements IShopifyGqlError {
  locations?: Array<{ line: number, column: number }>
  path?: Array<string | number>
  extensions?: Record<string, any>

  constructor({ message, locations, path, extensions }: IShopifyGqlError) {
    super(message)
    this.locations = locations
    this.path = path
    this.extensions = extensions

    Object.setPrototypeOf(this, ShopifyGqlError.prototype)
    this.name = 'ShopifyGqlError'
  }
}

export interface ShopifyGqlResponse<TData extends Record<string, any> = Record<string, any>> {
  data: TData;
  errors?: IShopifyGqlError[]
}

export interface IShopifyGqlUserError {
  field?: string[]
  message: string
}

export class ShopifyGqlUserError extends Error implements IShopifyGqlUserError {
  field?: string[]

  constructor({ field, message }: IShopifyGqlUserError) {
    super(message)
    this.field = field

    Object.setPrototypeOf(this, ShopifyGqlUserError.prototype)
    this.name = 'ShopifyGqlUserError'
  }
}

export interface ShopifyGqlUserErrors {
  userErrors?: IShopifyGqlUserError[]
}

export interface ShopifyGqlResult<TData extends Record<string, any> = Record<string, any>> {
  data: TData
  errors: (ShopifyGqlError | ShopifyGqlUserError)[]
}

export async function shopifyGql<TData extends Record<string, any> = Record<string, any>>(
  query: string, variables?: Record<string, any>
): Promise<ShopifyGqlResult<TData>> {
  const resp = await fetch(`https://${globalConstants.SHOPIFY_DOMAIN}/api/2025-04/graphql`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': globalConstants.SHOPIFY_STOREFRONT_API_KEY
    },
    body: JSON.stringify({
      query,
      variables
    })
  })
  if (resp.status >= 300) { throw new Error(`unexpected status code ${resp.status}`)}

  const body = await resp.json() as ShopifyGqlResponse<TData>
  const errors: Error[] = []

  if (body.errors) {
    errors.push(...body.errors.map((e: IShopifyGqlError) => new ShopifyGqlError(e)))
  }

  if (body.data) {
    const rootKey = Object.keys(body.data)[0] // name of operation under data
    const userErrors = (body.data as any)[rootKey]?.userErrors
    if (userErrors) {
      errors.push(...userErrors.map((e: IShopifyGqlUserError) => new ShopifyGqlUserError(e)))
    }
  }

  return {
    data: body.data,
    errors
  }
}
